import { useCallback, useState } from 'react';
import type { Screen } from '../../../shared/types';
import { useSnackbarStore } from '../../../shared/stores/useSnackbarStore';
import { convertBundledSettingsToProfile } from '../lib/convertBundledSettings';

interface UseConvertCurrentSettingsInput {
  userId: string | null | undefined;
  activeProjectKey: string | null;
  navigateTo: (screen: Screen) => void;
  /** Injected trust-binding writer — wires the new profile to the project for durability. */
  bindProfileToProject?: (profileKey: string, origin: 'legacy-conversion') => void;
}

interface UseConvertCurrentSettingsResult {
  handleConvert: () => void;
  isConverting: boolean;
}

/**
 * React hook for the "Convert current settings to profile" flow on the
 * Welcome page. Reads bundled PcfConfig from the plugin, creates a User
 * profile, and navigates directly to the Mapping Configuration page.
 */
export function useConvertCurrentSettings({
  userId,
  activeProjectKey,
  navigateTo,
  bindProfileToProject,
}: UseConvertCurrentSettingsInput): UseConvertCurrentSettingsResult {
  const [isConverting, setIsConverting] = useState(false);
  const showError = useSnackbarStore((state) => state.showError);
  const showSuccess = useSnackbarStore((state) => state.showSuccess);

  const handleConvert = useCallback(() => {
    if (isConverting) {
      return;
    }
    setIsConverting(true);

    convertBundledSettingsToProfile({ userId, activeProjectKey, bindProfileToProject })
      .then((result) => {
        if (result.success) {
          showSuccess('Current settings were saved as a reusable profile.');
          navigateTo('mappingConfiguration');
          return;
        }

        showError(`Current settings could not be converted: ${result.error}`);
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : String(error);
        showError(`Current settings could not be converted: ${message}`);
      })
      .finally(() => {
        setIsConverting(false);
      });
  }, [
    activeProjectKey,
    bindProfileToProject,
    isConverting,
    navigateTo,
    showError,
    showSuccess,
    userId,
  ]);

  return { handleConvert, isConverting };
}
